import type { RemoteLoadRequest, RemoteModule, RemoteTestOverrideRule } from './types';

export type RemoteModuleLoader = (request: RemoteLoadRequest) => Promise<RemoteModule>;

export class RemoteLoaderError extends Error {
  readonly remoteName: RemoteLoadRequest['remoteName'];
  readonly routePath: string;
  readonly correlationId: string;

  constructor(message: string, request: RemoteLoadRequest) {
    super(message);
    this.name = 'RemoteLoaderError';
    this.remoteName = request.remoteName;
    this.routePath = request.routePath;
    this.correlationId = request.correlationId;
  }
}

const remoteLoaders: Record<string, RemoteModuleLoader> = {
  productsListRoute: async () => {
    return import('./remotes/mfeProductsListRoute');
  },
  productsDeactivatedRoute: async () => {
    return import('./remotes/mfeProductsDeactivatedRoute');
  },
  productsDetailRoute: async () => {
    return import('./remotes/mfeProductsDetailRoute');
  },
  upcManagementRoute: async () => {
    return import('./remotes/mfeUpcManagementRoute');
  },
  upcReviewRoute: async () => {
    return import('./remotes/mfeUpcReviewRoute');
  },
  upcSubmitRoute: async () => {
    return import('./remotes/mfeUpcSubmitRoute');
  },
  platformSettingsRoute: async () => {
    return import('./remotes/mfePlatformSettingsRoute');
  },
  platformFieldOptionsRoute: async () => {
    return import('./remotes/mfePlatformFieldOptionsRoute');
  },
  platformAuthStartRoute: async () => {
    return import('./remotes/mfePlatformAuthStartRoute');
  },
  platformAuthCallbackRoute: async () => {
    return import('./remotes/mfePlatformAuthCallbackRoute');
  },
  platformAuthCompleteRoute: async () => {
    return import('./remotes/mfePlatformAuthCompleteRoute');
  },
  platformAcceptInviteRoute: async () => {
    return import('./remotes/mfePlatformAcceptInviteRoute');
  },
};

function getTestOverride(request: RemoteLoadRequest): RemoteTestOverrideRule | null {
  if (typeof window === 'undefined' || !window.__M360_REMOTE_TEST_OVERRIDES__) {
    return null;
  }

  return window.__M360_REMOTE_TEST_OVERRIDES__[request.moduleId] ?? null;
}

async function loadModuleWithOverride(
  loader: RemoteModuleLoader,
  request: RemoteLoadRequest
): Promise<RemoteModule> {
  const override = getTestOverride(request);

  if (!override) {
    return loader(request);
  }

  if (override.mode === 'reject') {
    throw new Error(override.message ?? 'Simulated remote failure');
  }

  if (override.mode === 'delay') {
    const delayMs = override.delayMs ?? request.timeoutMs + 250;
    await new Promise((resolve) => {
      window.setTimeout(resolve, delayMs);
    });
  }

  return loader(request);
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(`Remote load timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    promise
      .then((result) => {
        window.clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error: unknown) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

function resolveLoader(moduleId: string): RemoteModuleLoader {
  const loader = remoteLoaders[moduleId];
  if (loader) {
    return loader;
  }

  throw new Error(`No remote loader registered for moduleId: ${moduleId}`);
}

export async function loadRemoteModule(request: RemoteLoadRequest): Promise<RemoteModule> {
  try {
    const loader = resolveLoader(request.moduleId);
    return await withTimeout(loadModuleWithOverride(loader, request), request.timeoutMs);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown remote load failure';
    throw new RemoteLoaderError(message, request);
  }
}
